import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';
import { getDayDiff } from '../utils/dateUtils';
import { getThemeStyle } from '../utils/themeUtils';
import SafeIcon from '../common/SafeIcon';

export default function PreviewModal({ isOpen, onClose, item, onEdit, onDelete, onShare }) {
  const { t } = useLocale();
  const { isDark } = useTheme();

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // 修正：僅阻擋 item 缺失，確保 isOpen 變化時 AnimatePresence 能觸發 exit 動畫
  if (!item) return null;

  const { diffDays, status, weekDayIndex } = getDayDiff(new Date(), item.targetDateISO);
  const currentStyle = getThemeStyle(item.theme, status, isDark);
  
  let label = t('common.remaining');
  if (status === 'today') label = t('common.justToday');
  if (status === 'elapsed') label = t('common.passed');

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center px-4 sm:px-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-[#fffbf0] dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] shadow-2xl relative z-10 flex flex-col border-4 border-white dark:border-slate-800 overflow-hidden"
          >
            {/* Header / Close Button */}
            <div className="absolute top-4 right-4 z-20">
              <button
                onClick={onClose}
                className="p-2 bg-white/40 dark:bg-black/30 hover:bg-white/60 dark:hover:bg-black/50 text-gray-800 dark:text-gray-200 rounded-full backdrop-blur-md transition-colors"
              >
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>

            {/* Preview Card Content */}
            <div className={`pt-12 pb-8 px-6 flex flex-col items-center text-center bg-gradient-to-br ${currentStyle.bg} relative`}>
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white opacity-20 rounded-full blur-2xl pointer-events-none"></div>
              
              <div className="w-20 h-20 mb-4 bg-white dark:bg-slate-800 rounded-[1.5rem] shadow-lg flex items-center justify-center text-4xl transform rotate-3">
                {item.icon}
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-black mb-3 text-gray-900 dark:text-white break-words max-w-full drop-shadow-sm flex flex-col items-center gap-2">
                {item.isDemo && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/50 dark:bg-black/30 backdrop-blur-md border border-white/40 dark:border-white/10 text-pink-600 dark:text-pink-300 text-[10px] font-black tracking-widest shadow-sm">
                    ✨ {t('common.demoBadge')}
                  </span>
                )}
                {t(item.title)}
              </h2>
              
              <div className="bg-white/40 dark:bg-black/20 px-4 py-1.5 rounded-full font-bold text-sm mb-6 shadow-sm text-gray-800 dark:text-gray-200 backdrop-blur-sm">
                {item.targetDateISO} {t(`date.weekdays.${weekDayIndex}`)}
              </div>

              <div className="flex flex-col items-center relative z-10">
                <span className="text-gray-700 dark:text-gray-300 font-bold mb-1 drop-shadow-sm">{label}</span>
                {status !== 'today' ? (
                  <div className={`flex items-baseline gap-2 ${currentStyle.text}`}>
                    <span className="text-7xl font-black drop-shadow-md">{diffDays}</span>
                    <span className="text-2xl font-bold">{t('common.days')}</span>
                  </div>
                ) : (
                  <span className="text-7xl font-black drop-shadow-md">🎉</span>
                )}
              </div>

              {item.note && (
                <div className="mt-6 w-full bg-white/50 dark:bg-black/30 p-4 rounded-2xl text-left text-gray-800 dark:text-gray-200 font-medium backdrop-blur-sm text-sm border border-white/30 dark:border-white/10">
                  💡 {t(item.note)}
                </div>
              )}
            </div>

            {/* Action Bar */}
            <div className="p-4 bg-white dark:bg-slate-900 flex gap-3 z-10">
              <button
                onClick={onDelete}
                className="p-4 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors shadow-sm"
                title={t('modal.deleteBtn')}
              >
                <SafeIcon name="Trash2" className="w-6 h-6" />
              </button>
              <button
                onClick={onShare}
                className="p-4 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-2xl hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors shadow-sm"
                title={t('common.share')}
              >
                <SafeIcon name="Share2" className="w-6 h-6" />
              </button>
              <button
                onClick={onEdit}
                className="flex-1 p-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-lg rounded-2xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 shadow-lg active:scale-95"
              >
                <SafeIcon name="Edit2" className="w-5 h-5" />
                {t('modal.editBtn')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}